import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { LOCAL_STORAGE_KEYS, orderMap } from '@constants';
import { CAMPAIGN_STATUS } from '@enums';
import { useConnector, useStore } from '@hooks';
import { Loader, Table } from '@ui';
import { camelToSnakeCase } from '@utils';
import { observer } from 'mobx-react';
import DemoProductsModal from '../../components/DemoProductsModal';
import CampaignProductsTable from './components/CampaignProductsTable';
import EmptyState from './components/EmptyState';
import Header from './components/Header';
import NotConnected from './components/NotConnected';
import {
    isDefaultStatusesFilter,
    readCampaignFiltersFromStorage,
    saveCampaignFiltersToStorage
} from './utils/campaignFiltersStorage';
import { CAMPAIGNS_FILTER_DEFAULTS, getColumns } from './constants';

const CampaignsPage = observer(() => {
    const store = useStore();
    const {
        campaigns,
        filters,
        sorters,
        meta,
        hasAnyCampaign,
        getCampaigns,
        checkHasAnyCampaign,
        getCampaignProducts,
        pending,
        updateSorters,
        updateFilters,
        resetFilters,
        resetSorters,
        reFetchCampaign,
        getCampaign
    } = store.campaignsStore;
    const {
        isActive: isAccountActive,
        isShopify,
        currentAccount,
        isManualWithSyncInProgressOrFailed
    } = store.accountsStore;

    const { connected } = useConnector();

    const [loading, setLoading] = useState(true);
    const [isDemoProductsModalOpen, setIsDemoProductsModalOpen] = useState(false);

    useEffect(() => {
        (async () => {
            const storedFilters = readCampaignFiltersFromStorage();
            const filtersToApply = storedFilters ?? CAMPAIGNS_FILTER_DEFAULTS;

            updateFilters({ statuses: filtersToApply.statuses, catalog_types: filtersToApply.catalog_types });
            updateSorters({
                sort: filtersToApply.sort ?? CAMPAIGNS_FILTER_DEFAULTS.sort,
                order: filtersToApply.order ?? CAMPAIGNS_FILTER_DEFAULTS.order
            });

            const processingCampaignId = localStorage.getItem(LOCAL_STORAGE_KEYS.PROCESSING_CAMPAIGN);
            const showDemoModal = localStorage.getItem(LOCAL_STORAGE_KEYS.SHOW_DEMO_PRODUCTS_MODAL) === 'true';

            await Promise.all([getCampaigns(), checkHasAnyCampaign()]);

            // Case: default statuses matched nothing, but the account does have campaigns
            // so drop the status filter instead of showing a confusing zero-state.
            const isUntouchedDefaultView = isDefaultStatusesFilter(filtersToApply.statuses);
            if (
                !store.campaignsStore.campaigns.length &&
                store.campaignsStore.hasAnyCampaign &&
                isUntouchedDefaultView
            ) {
                updateFilters({ statuses: undefined });
                await getCampaigns();
            }

            if (processingCampaignId) {
                localStorage.removeItem(LOCAL_STORAGE_KEYS.PROCESSING_CAMPAIGN);
                const campaign = getCampaign(Number(processingCampaignId));
                if (campaign) {
                    campaign.setPending(true);
                    reFetchCampaign(Number(processingCampaignId), [
                        CAMPAIGN_STATUS.ACTIVE,
                        CAMPAIGN_STATUS.PAUSED,
                        CAMPAIGN_STATUS.CREATED,
                        CAMPAIGN_STATUS.DELETED,
                        CAMPAIGN_STATUS.FROZEN,
                        CAMPAIGN_STATUS.PRE_LAUNCHED
                    ]);
                }
            }

            if (showDemoModal) {
                setIsDemoProductsModalOpen(true);
                localStorage.setItem(LOCAL_STORAGE_KEYS.SHOW_DEMO_PRODUCTS_MODAL, 'false');
            }

            setLoading(false);
        })();

        return () => {
            resetFilters();
            resetSorters();
        };
    }, []);

    const handleTableChange = (pagination, tableFilters, sorter) => {
        const page = pagination?.current;
        // orderMap converts Ant Design 'ascend'/'descend' to API-expected 'asc'/'desc'
        const newSorters = sorter.order
            ? { sort: camelToSnakeCase(sorter.field), order: orderMap[sorter.order] }
            : { sort: sorters.sort, order: sorters.order };
        const newFilters = {
            statuses: tableFilters.status || undefined,
            catalog_types: tableFilters.catalogType || undefined
        };

        updateSorters(newSorters);
        updateFilters(newFilters);
        saveCampaignFiltersToStorage({ filters: newFilters, sorters: newSorters });

        getCampaigns({ page });
    };

    const columns = useMemo(
        () => getColumns(currentAccount?.platformId, filters, sorters),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentAccount?.platformId, filters.statuses, filters.catalog_types, sorters.sort, sorters.order]
    );

    const handleSearch = (search) => {
        updateFilters({
            search: search ? `title:${search}` : undefined
        });
        getCampaigns();
    };

    if (loading) return <Loader size='120px' />;

    if (isManualWithSyncInProgressOrFailed) {
        return <EmptyState />;
    }

    if (!connected && isShopify) {
        return <NotConnected />;
    }

    if (!hasAnyCampaign && !campaigns.length) {
        return <EmptyState />;
    }

    return (
        <Fragment>
            <Header setSearchValue={handleSearch} />
            <Table
                columns={columns}
                dataSource={campaigns}
                rowKey={(record) => record.id}
                loading={pending}
                pagination={{
                    pageSize: meta.perPage,
                    total: meta.total,
                    current: meta.page,
                    totalPages: meta.totalPages
                }}
                onChange={handleTableChange}
                expandable={
                    isAccountActive
                        ? {
                              onExpand: (expanded, record) => (expanded ? getCampaignProducts(record.id) : null),
                              expandedRowRender: (record) => {
                                  return <CampaignProductsTable campaignId={record.id} />;
                              }
                          }
                        : false
                }
                data-e2e='campaigns_campaigns-list_table'
                scroll={{
                    x: 1250
                }}
            />
            <DemoProductsModal isModalOpen={isDemoProductsModalOpen} setIsModalOpen={setIsDemoProductsModalOpen} />
        </Fragment>
    );
});
export default CampaignsPage;
